import { clsx } from 'clsx';
import { Children, type PropsWithChildren } from 'react';
import { useIntl } from 'react-intl';

import {
  OverlayRightIcon,
  SplitPanelRightIcon,
  XmarkIcon,
} from '@dynatrace/strato-icons';

import * as styles from './DetailsControlBar.sty.js';
import { useDetailsLayoutContext } from './DetailsLayoutContext.js';
import { Button } from '../../../buttons/button/Button.js';
import type { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { MaskingProps } from '../../../core/types/masking-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import { Divider } from '../../divider/Divider.js';

/**
 * Props for the `DetailsControlBar` component that renders layout and close controls inside the details panel.
 * @public
 */
export interface DetailsControlBarProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}

/** @internal */
export const DetailsControlBar = (props: DetailsControlBarProps) => {
  const { children, className, ...restProps } = props;
  const context = useDetailsLayoutContext();
  const intl = useIntl();

  const hasCustomActions = Children.count(children) > 0;

  const closeLabel = intl.formatMessage({
    defaultMessage: 'Close details',
    id: 'UK0OoP7XF++evklW',
    description: 'Label for the button that closes the details panel.',
  });

  const switchToOverlayLabel = intl.formatMessage({
    defaultMessage: 'Switch to overlay',
    id: '+mEjL2q8RACwzIMR',
    description:
      'Label for the button that switches the details panel to overlay mode.',
  });

  const switchToSplitLabel = intl.formatMessage({
    defaultMessage: 'Switch to side-by-side',
    id: 'RST92XLweEz0Qott',
    description:
      'Label for the button that switches the details panel to split mode.',
  });

  const handleClose = () => {
    context?.setCollapsed(true);
  };

  const handleLayoutToggle = () => {
    if (!context) {
      return;
    }

    context.setPreferredLayout((prev) =>
      prev === 'split' ? 'overlay' : 'split',
    );
  };

  const showLayoutToggle = context?.mode
    ? ['overlay', 'split'].includes(context.mode)
    : false;
  const isSplit = context?.preferredLayout === 'split';

  return (
    <div {...restProps} className={clsx(styles.controlBar, className)}>
      {hasCustomActions && (
        <div className={styles.customActions}>{children}</div>
      )}
      {hasCustomActions && (
        <Divider orientation="vertical" as="hr" flexItem aria-hidden />
      )}
      <div className={styles.defaultActions}>
        {showLayoutToggle && (
          <Button
            aria-label={isSplit ? switchToOverlayLabel : switchToSplitLabel}
            onClick={handleLayoutToggle}
          >
            <Button.Prefix>
              {isSplit ? <OverlayRightIcon /> : <SplitPanelRightIcon />}
            </Button.Prefix>
          </Button>
        )}
        <Button
          aria-label={closeLabel}
          onClick={handleClose}
          data-dt-children-name="CloseX"
        >
          <Button.Prefix>
            <XmarkIcon />
          </Button.Prefix>
        </Button>
      </div>
    </div>
  );
};

DetailsControlBar.displayName = 'PageLayout.Details.ControlBar';
